import React, { useRef, useState, useEffect } from "react";
import { motion, useMotionValue, useAnimationFrame, animate } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { PARTNERS } from "@/data/services";

const PartnerPill = ({ name, logo }) => (
  <div
    className="mx-3 flex-shrink-0 inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-6 py-3 ring-soft h-16 w-28 sm:h-20 sm:w-36"
    data-testid={`partner-${name.replace(/\s+/g, "-").toLowerCase()}`}
  >
    {logo ? (
      <img
        src={logo}
        alt={name}
        className="max-h-[52px] w-auto object-contain"
        loading="lazy"
      />
    ) : (
      <span className="font-heading font-bold text-sm tracking-wide text-slate-700 uppercase">{name}</span>
    )}
  </div>
);

const SPEED = 32; // px/seconde -indépendant du refresh rate (60 ou 120 Hz)

const PartnersCarousel = () => {
  const { t } = useTranslation();
  const trackRef = useRef(null);
  const paused = useRef(false);
  const dragging = useRef(false);
  const resumeTimer = useRef(null);
  const x = useMotionValue(0);
  const [halfWidth, setHalfWidth] = useState(1200);

  useEffect(() => {
    const compute = () => {
      if (trackRef.current) setHalfWidth(trackRef.current.offsetWidth / 2);
    };
    compute();
    const ro = new ResizeObserver(compute);
    if (trackRef.current) ro.observe(trackRef.current);
    return () => ro.disconnect();
  }, []);

  // Loop infini fluide : déplacement basé sur le temps (delta), vitesse constante
  // quel que soit le refresh rate, wrap sans à-coup (préserve le sous-pixel).
  useAnimationFrame((_, delta) => {
    if (paused.current || dragging.current || !delta) return;
    const d = Math.min(delta, 64); // évite un saut brusque au retour d'onglet
    let next = x.get() - (SPEED * d) / 1000;
    if (next <= -halfWidth) next += halfWidth;
    x.set(next);
  });

  const pauseAndResumeLater = (delayMs = 800) => {
    paused.current = true;
    clearTimeout(resumeTimer.current);
    resumeTimer.current = setTimeout(() => { paused.current = false; }, delayMs);
  };

  // Slide bouton -spring animée pour fluidité
  const slide = (dir) => {
    const next = Math.max(-halfWidth, Math.min(0, x.get() + dir * 240));
    animate(x, next, { type: "spring", stiffness: 280, damping: 28, mass: 1 });
    pauseAndResumeLater(1200);
  };

  return (
    <section className="py-12 sm:py-14 bg-white" data-testid="partners-marquee-section">
      <div className="container-x mb-6">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-blue">{t("trust.title")}</p>
        <h2 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 tracking-tight mt-1">
          {t("trust.subtitle")}
        </h2>
      </div>

      <div
        className="relative"
        onMouseEnter={() => { paused.current = true; }}
        onMouseLeave={() => {
          if (!dragging.current) paused.current = false;
        }}
      >
        <button
          onClick={() => slide(1)}
          className="hidden lg:flex absolute left-4 top-1/2 -translate-y-1/2 z-10 h-9 w-9 items-center justify-center rounded-full bg-white border border-slate-200 shadow-sm hover:border-brand-green hover:text-brand-green text-slate-500 transition-all"
          aria-label="Précédent"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button
          onClick={() => slide(-1)}
          className="hidden lg:flex absolute right-4 top-1/2 -translate-y-1/2 z-10 h-9 w-9 items-center justify-center rounded-full bg-white border border-slate-200 shadow-sm hover:border-brand-green hover:text-brand-green text-slate-500 transition-all"
          aria-label="Suivant"
        >
          <ChevronRight className="h-4 w-4" />
        </button>

        <div className="pointer-events-none absolute left-0 top-0 h-full w-12 sm:w-20 bg-gradient-to-r from-white to-transparent z-[1]" />
        <div className="pointer-events-none absolute right-0 top-0 h-full w-12 sm:w-20 bg-gradient-to-l from-white to-transparent z-[1]" />

        <div className="overflow-hidden px-4">
          <motion.div
            ref={trackRef}
            style={{ x, touchAction: "none" }}
            drag="x"
            dragConstraints={{ left: -halfWidth, right: 0 }}
            dragElastic={0.08}
            dragMomentum={true}
            dragTransition={{ power: 0.2, timeConstant: 250, bounceStiffness: 300, bounceDamping: 40 }}
            className="flex w-max cursor-grab active:cursor-grabbing py-2 will-change-transform"
            onDragStart={() => {
              dragging.current = true;
              paused.current = true;
              clearTimeout(resumeTimer.current);
            }}
            onDragEnd={() => {
              dragging.current = false;
              pauseAndResumeLater(900);
            }}
            data-testid="partners-marquee"
          >
            {[...PARTNERS, ...PARTNERS].map((p, i) => (
              <PartnerPill key={`${p.name}-${i}`} name={p.name} logo={p.logo} />
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PartnersCarousel;
